import { EXTEND_STAY_OPTIONS, PageTypeEnum } from '@/constant';
import {
	GetApplicantByFranchiseExtensionStayListResponse,
	GetApplicantListByIdsResponse,
	GetExtensionStayListResponse,
	GetWorkerExtensionStayListResponse,
	IApplicantByFranchiseExtensionStay,
	IApplicantForExtensionStay,
	IApplicantListItem,
	IFranchiseListItem,
} from '@/services';

import {
	formatDate,
	getDiffExpirationDateString,
	insertObjectIfV2,
	map,
} from 'utils';

import { isNotEmpty } from 'ramda';

import { formatWorkerApplicantListItem } from '../worker-applicant';
import { formatWorkerFranchiseListItem } from '../worker-franchise';

export const formatWorkerExtensionStayDetail = (
	input: GetWorkerExtensionStayListResponse['content'][number]
) => {
	return {
		...input,
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
	};
};

export const formatWorkerExtensionStayList = (
	input: GetWorkerExtensionStayListResponse['content']
) => {
	return map(input, formatWorkerExtensionStayDetail);
};

export const formatApplicantByFranchiseExtensionStayDetail = (
	input: GetApplicantByFranchiseExtensionStayListResponse[number]
) => {
	return {
		...input,
		id: input?.applicantId,
		birthDate: formatDate(input?.birthDate),
		entryDate: formatDate(input?.entryDate),
		expirationDateText: getDiffExpirationDateString(input?.expirationDate),
	};
};

export const formatApplicantByFranchiseExtensionStayList = (
	input: IApplicantByFranchiseExtensionStay[]
) => {
	return map(input, formatApplicantByFranchiseExtensionStayDetail);
};

export const formatApplicantListByIdsDetail = (
	input: GetApplicantListByIdsResponse[number]
) => {
	return {
		...input,
		extensionDate: input?.extensionStayDate,
		expirationDateText: getDiffExpirationDateString(input?.expirationDate),
		entryDate: formatDate(input?.entryDate),
		birthDate: formatDate(input?.birthDate),
	};
};

export const formatApplicantListByIds = (
	input: GetApplicantListByIdsResponse
) => {
	return map(input, formatApplicantListByIdsDetail);
};

export const formatApplicantForExtensionStayDetail = (
	input: IApplicantForExtensionStay
) => {
	return {
		...input,
		birthDate: formatDate(input?.birthDate),
		entryDate: formatDate(input?.entryDate),
		extensionDate: formatDate(input?.extensionDate),
		expirationDateText: getDiffExpirationDateString(input?.expirationDate),
	};
};

export const formatApplicantForExtensionStayList = (
	input: IApplicantForExtensionStay[]
) => {
	return map(input, formatApplicantForExtensionStayDetail);
};

const filterByPrefixKeyWithFormat = <T extends object>(
	obj: T,
	prefix: PageTypeEnum
) => {
	const result = {} as T;
	for (const key in obj) {
		if (key.startsWith(prefix?.toLocaleLowerCase())) {
			result[key] = obj[key];
		}
	}

	return result;
};

export const formatExtensionStayDetail = (
	input: GetExtensionStayListResponse['content'][number]
) => {
	let _input = input;

	let newFranchiseFormat = filterByPrefixKeyWithFormat<IFranchiseListItem>(
		_input,
		PageTypeEnum.FRANCHISE
	);
	if (isNotEmpty(newFranchiseFormat)) {
		newFranchiseFormat = formatWorkerFranchiseListItem(newFranchiseFormat);
	}

	let newApplicantFormat = filterByPrefixKeyWithFormat<IApplicantListItem>(
		_input,
		PageTypeEnum.APPLICANT
	);
	if (isNotEmpty(newApplicantFormat)) {
		newApplicantFormat = formatWorkerApplicantListItem(newApplicantFormat);
	}

	const formatValues = {
		...newFranchiseFormat,
		...newApplicantFormat,
	} as typeof _input;

	return {
		...input,
		...insertObjectIfV2(isNotEmpty(formatValues), {
			...formatValues,
		}),
		id:
			(input?.extensionStayApplicantId ||
				input?.extensionStayApplicantExtensionStayId) ??
			input?.extensionStayApplicantId,
		extensionStayApplicantRegistDt: formatDate(
			input?.extensionStayApplicantRegistDt
		),
		extensionStayApplicantUpdateDt: formatDate(
			input?.extensionStayApplicantUpdateDt
		),
		extensionStayApplicantExpirationDate: formatDate(
			input?.extensionStayApplicantExpirationDate
		),
		extensionStayApplicantExtensionPeriod:
			EXTEND_STAY_OPTIONS.find(
				(item) => item?.value === input?.extensionStayApplicantExtensionPeriod
			)?.label || input?.extensionStayApplicantExtensionPeriod,
		extensionStayId:
			input?.extensionStayId || input?.extensionStayApplicantExtensionStayId,
	};
};

export const formatExtensionStayList = (
	input: GetExtensionStayListResponse['content']
) => {
	return map(input, formatExtensionStayDetail);
};

export const formatExtensionStayExcelList = (
	input: GetExtensionStayListResponse['content']
) => {
	return map(input, (item) => {
		return {
			...formatExtensionStayDetail(item),
		};
	});
};
